import axiosInstance from "../lib/axios.instance";
import type {
  ReviewDefinitionsResponse,
  StoredResponseRequest,
  StoredResponseResult,
  GenerateReviewRequest,
  RemoveTileRequest,
  DismissAllTilesRequest,
  UpdateTileStatusRequest,
  EditTileRequest,
  BulkWriteItRequest,
  WriteItRequest,
} from "../types/review.types";

const BASE_URL = "/ai";

/**
 * Fetch available review definitions from the API
 */
export const getDefinitions = async (): Promise<ReviewDefinitionsResponse> => {
  try {
    const response = await axiosInstance.get<ReviewDefinitionsResponse>(`${BASE_URL}/definitions/`);
    return response.data;
  } catch (error) {
    console.error("Error fetching review definitions:", error);
    throw error;
  }
};

/**
 * Generate review with query terms
 */
export const generateReview = async (
  request: GenerateReviewRequest,
  signal?: AbortSignal
): Promise<StoredResponseResult> => {
  try {
    const { query_term, ...payload } = request;
    const response = await axiosInstance.post<StoredResponseResult>(
      `${BASE_URL}/document/query/${query_term}`,
      payload,
      { signal }
    );
    return response.data;
  } catch (error) {
    console.error("Error generating review:", error);
    throw error;
  }
};

/**
 * Get stored response for document query
 */
export const getStoredResponse = async (
  request: StoredResponseRequest
): Promise<StoredResponseResult> => {
  try {
    const response = await axiosInstance.post<StoredResponseResult>(
      `${BASE_URL}/document/query/response/stored-response`,
      request
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching stored response:", error);
    throw error;
  }
};

/**
 * Format option key into readable text
 */
const formatOptionText = (key: string): string => {
  return key
    .split("-")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

/**
 * Remove tile from document query response
 */
export const removeTile = async (request: RemoveTileRequest): Promise<void> => {
  try {
    await axiosInstance.delete(`${BASE_URL}/document/query/response/remove-tile`, {
      data: request,
    });
  } catch (error) {
    console.error("Error removing tile:", error);
    throw error;
  }
};

/**
 * Dismiss all tiles for a document and query terms.
 */
export const dismissAllTiles = async (request: DismissAllTilesRequest): Promise<void> => {
  try {
    await axiosInstance.delete(`${BASE_URL}/document/query/response/remove-document-responses`, {
      data: request,
    });
  } catch (error) {
    console.error("Error dismissing all tiles:", error);
    throw error;
  }
};

/**
 * Process review definitions into structured options
 */
export const processDefinitions = (definitions: ReviewDefinitionsResponse) => {
  const options: Array<{ key: string; text: string; category: string }> = [];

  // Process regular reviews, filtering out compare-precedent
  definitions.reviews.forEach((item) => {
    if (typeof item === "string") {
      options.push({
        key: item,
        text: formatOptionText(item),
        category: "review",
      });
    }
    // Skip compare-precedent options as per requirements
  });

  return options;
};

/**
 * Update the status of a tile
 */
export const updateTileStatus = async (data: UpdateTileStatusRequest): Promise<any> => {
  try {
    const response = await axiosInstance.post(`/ai/document/query/response/update-status`, data);
    return response.data;
  } catch (error) {
    console.error("Error updating tile status:", error);
    throw error;
  }
};

/**
 * Edit a tile
 */
export const editTile = async (request: EditTileRequest): Promise<any> => {
  try {
    const response = await axiosInstance.put(
      `${BASE_URL}/document/query/response/edit-tile`,
      request
    );
    return response.data;
  } catch (error) {
    console.error("Error editing tile:", error);
    throw error;
  }
};

/**
 * Bulk write it for multiple tiles
 */
export const bulkWriteIt = async (request: BulkWriteItRequest): Promise<any> => {
  try {
    const response = await axiosInstance.post(`${BASE_URL}/document/wifm/`, request);
    return response.data;
  } catch (error) {
    console.error("Error during bulk write it:", error);
    throw error;
  }
};

/**
 * Write it for me for a single tile
 */
export const writeIt = async (request: WriteItRequest): Promise<any> => {
  try {
    const response = await axiosInstance.post(
      `${BASE_URL}/document/wifm/${request.query_term}`,
      request
    );
    return response.data;
  } catch (error) {
    console.error("Error during write it:", error);
    throw error;
  }
};
